import { motion } from 'framer-motion/dist/framer-motion';
import { CustomPagination } from 'pages/home/Home.style';
import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import SwiperCore, { A11y, Autoplay, Lazy, Navigation, Pagination } from 'swiper';
import 'swiper/components/effect-coverflow/effect-coverflow.scss';
import 'swiper/components/navigation/navigation.scss';
import 'swiper/components/pagination/pagination.scss';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.scss';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { flexGap } from 'untils/polyfill/flex';

SwiperCore.use([Lazy, Navigation, A11y, Pagination, Autoplay]);

const testimonials = [
  {
    name: 'Ram S.',
    text: 'The team was very resourceful in helping me find the right flight that got me to my destination without an issue! They are a pleasure to work with! Seamless Transaction! I would definitely recommend!',
    images: [
      {
        src: '/imgs/webp/home/testimonial5.webp',
        alt: 'cheap private jet charters',
      },
    ],
  },
  {
    name: 'Chao Q.',
    text: 'I totally love their honesty about the aircraft and enjoy the whole trip from Vancouver to Cabos with my family. Good price and great services!',
    images: [
      {
        src: '/imgs/webp/home/testimonial6.webp',
        alt: 'cheap private jet charters',
      },
    ],
  },
  {
    name: 'Leah M.',
    text: 'This was my first time chartering a flight and I had many questions. Tina was patient enough to answer all my questions with detailed explanations till she made sure I was all clear. The flight itself was amazing. It’s very smooth. We brought our own food and wine onboard, and the flight attendant kindly helped us heat the food and do other preparations.',
    images: [
      {
        src: '/imgs/webp/home/testimonial1.webp',
        alt: 'luxury private jet charters',
      },
    ],
  },
  {
    name: 'Chase R.',
    text: 'The pilots were amazing and very nice. The ride not only saved us time but was very smooth. The snacks on board where very good as well as the ice cold drinks they had in the cooler.',
    images: [
      {
        src: '/imgs/webp/home/testimonial2.webp',
        alt: 'private jet rental',
      },
    ],
  },
  {
    name: 'Lifu L.',
    text: 'We had a great experience with Airacer starting from requesting a quote to flight landing. Amazing aircraft and very friendly crew. Cecilia helped with all my questions and requests of catering and decoration for our anniversary. Truly satisfied with their work.',
    images: [
      {
        src: '/imgs/webp/home/testimonial3.webp',
        alt: 'private charter jet',
      },
    ],
  },
  {
    name: 'Kathy M.',
    text: 'Airacer staff provided exceptional service, and the journey was incredibly smooth. I truly appreciated the privacy and personalized catering onboard. It made the experience so enjoyable.',
    images: [
      {
        src: '/imgs/webp/home/testimonial4.webp',
        alt: 'fly charter flight',
      },
    ],
  },
];

const TestContainer = styled.div`
  width: var(--home-section-width);
  padding: var(--home-section-top-padding) 0;

  .swiper-slide {
    font-size: 18px;
    background: #fff;
    display: flex;
    justify-content: center;
    align-items: center;
    width: clamp(279px, 100%, 415px);
    height: 320px;
    @media (max-width: 757px) {
      width: 340px;
      height: 167px;
    }
  }
  @media (max-width: 1380px) {
    padding-right: 0;
  }

  @media (max-width: 757px) {
    margin-top: 24px;
    margin-bottom: 48px;
  }
`;

const CustomerItem = styled(motion.div)`
  position: relative;
  display: flex;
  width: 600px;
  height: 100%;
  overflow: hidden;
  ${flexGap('16px')}
  cursor: pointer;
  & > * {
    flex: 1;
  }
`;

const TextContent = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: space-between;
  flex-direction: column;
  font-weight: 400;
  font-size: 16px;
  color: #424748;
  line-height: 1.3;
  p {
    max-height: 500px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 9;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  }
`;

const Name = styled.span`
  font-weight: 600;
  font-size: 16px;
  color: #050a30;
`;

const ImageContent = styled.div`
  position: relative;
`;

const Image = styled(motion.img)`
  height: ${(props) => props.$height && `${props.$height}%`};
  border-radius: 2px;
  object-fit: cover;

  @media (hover: hover) {
    &:hover {
      position: absolute;
      width: 100%;
      height: 100%;
      transition: all 0.1s ease-in-out;
      &:nth-child(odd) {
        top: 0;
        left: 0;
      }
      &:nth-child(even) {
        bottom: 0;
        left: 0;
      }
    }
  }
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 50px;
`;

const Title = styled.h2`
  font-weight: 600;
  font-size: 40px;
  margin: 0;
  line-height: normal;
`;

const Control = styled.div`
  width: fit-content;
  display: flex;
  ${flexGap('16px')}
`;

const ControlButton = styled.button`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  cursor: pointer;
  background: #f4f6fc;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SquarePointer = styled(motion.div)`
  border: 2px solid #07080a;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  & > img {
    width: 24px;
    height: 24px;
  }
`;

const iconMotion = {
  hover: {
    backgroundColor: '#07080a',
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

export const CirclePointerIcon = (props) => {
  return (
    <SquarePointer variants={iconMotion} {...props}>
      <motion.svg
        initial={{ color: '#07080a' }}
        exit={{ color: '#07080a' }}
        variants={{
          hover: {
            color: '#fff',
            transition: {
              duration: 0.2,
              ease: 'easeIn',
            },
          },
        }}
        width="13"
        height="22"
        viewBox="0 0 13 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.48535 19.4852L10.9706 10.9999L2.48535 2.51467"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.svg>
    </SquarePointer>
  );
};

const CustomersFeedback1 = () => {
  const sliderRef = useRef(null);

  const handlePrev = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slidePrev();
  }, []);

  const handleNext = useCallback(() => {
    if (!sliderRef.current) return;
    sliderRef.current.swiper.slideNext();
  }, []);

  return (
    <TestContainer>
      <Header>
        <Title>
          Here's what our customers
          <br /> have to say:
        </Title>
        <Control>
          <ControlButton onClick={handlePrev}>
            <ArrowLeft color="#050A30" strokeWidth={1.5} />
          </ControlButton>
          <ControlButton onClick={handleNext}>
            <ArrowRight color="#050A30" strokeWidth={1.5} />
          </ControlButton>
        </Control>
        <CustomPagination id="custom-pagination" />
      </Header>

      <Swiper
        slidesPerView={3}
        slidesPerGroup={3}
        spaceBetween={50}
        lazy
        loop
        ref={sliderRef}
        autoplay={{
          delay: 3000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        pagination={{
          renderCustom: function (_, current, total) {
            return `<span>${current}</span> / <span>${total}</span>`;
          },
          type: 'custom',
          el: '#custom-pagination',
        }}
      >
        {testimonials.map((item, i) => {
          return (
            <SwiperSlide key={i}>
              <CustomerItem>
                <ImageContent>
                  {item.images.map((image, key) => {
                    const amount = item.images.length;
                    return (
                      <Image
                        $height={amount > 1 ? 100 / amount : 100}
                        key={key}
                        src={image.src}
                        alt={image.alt}
                      />
                    );
                  })}
                </ImageContent>

                <TextContent>
                  <p>{item.text}</p>
                  <Name>{item.name}</Name>
                </TextContent>
              </CustomerItem>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </TestContainer>
  );
};

export default CustomersFeedback1;
